/**
 * "Allow re-flash" confirmation (JSS#883 security review).
 *
 * A box that has proven its own reconnect key rejoins from the site's USB
 * image only inside a window an Admin opens (`POST /machines/:id/allow-reflash`,
 * 24 h here). Without it, the USB plus the box's hardware ID — which isn't
 * secret — would be enough to take over its identity. So it asks first, and
 * says when a rejoin was refused: if nobody here re-flashed the box, the
 * refused box may be someone else's.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ShieldCheck, X } from 'lucide-react';
import { machinesApi } from '@/api/client';
import { recoveryErrorMessage, REFLASH_WINDOW_HOURS, reflashApproval } from '@/lib/boxRecovery';
import { formatAge } from '@/lib/boxes';
import type { Machine } from '@/types';

interface Props {
  machine: Machine;
  onClose: () => void;
}

export default function AllowReflashDialog({ machine, onClose }: Props) {
  const queryClient = useQueryClient();
  const now = Date.now();
  const approval = reflashApproval(machine, now);
  const refusedAt = approval.kind === 'offer' ? approval.refusedAt : null;

  const mutation = useMutation({
    mutationFn: () => machinesApi.allowReflash(machine.id, REFLASH_WINDOW_HOURS),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      onClose();
    },
  });
  const err = mutation.isError ? recoveryErrorMessage(mutation.error, 'allow-reflash') : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Allow re-flash: ${machine.hostname}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-gold/10">
              <ShieldCheck size={18} className="text-gold" />
            </div>
            <div>
              <h3 className="text-silver-bright font-semibold">Allow re-flash</h3>
              <p className="text-xs text-silver-muted font-mono">{machine.hostname}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-silver-muted hover:text-silver-bright" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 text-sm text-silver-muted">
          <p className="text-silver-bright">
            Use this if you re-flashed this box from the USB image. It lets the box rejoin as itself.
          </p>
          {refusedAt && (
            <div
              className="flex gap-2 border rounded-lg px-3 py-2 text-xs bg-status-warning/10 border-status-warning/30 text-status-warning"
              data-testid="allow-reflash-refused"
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <p>
                A box tried to rejoin as {machine.hostname} from the USB image{' '}
                {formatAge(now - new Date(refusedAt).getTime())} ago and was refused.
              </p>
            </div>
          )}
          <p className="text-xs">
            If you didn&apos;t re-flash it, don&apos;t allow this. The permission ends after {REFLASH_WINDOW_HOURS} hours.
          </p>

          {err && (
            <div
              className="text-xs text-status-error bg-status-error/10 border border-status-error/30 rounded px-3 py-2"
              data-testid="allow-reflash-error"
            >
              {err.message}
              {err.hint && <p className="mt-1 text-silver-bright">{err.hint}</p>}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            data-testid="confirm-allow-reflash"
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-gold text-surface-deep hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Working…' : `Allow for ${REFLASH_WINDOW_HOURS} hours`}
          </button>
        </div>
      </div>
    </div>
  );
}
